import { useEffect, useRef, useState } from "react";
import { api, formatApiError, apiError, uploadFiles } from "@/lib/api";
import { SignedImg } from "@/components/shared/SignedImg";
import { PhotoLightbox } from "@/components/shared/PhotoLightbox";
import { compressImage } from "@/lib/image";
import { formatDate, formatTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Minus, Plus, X, Trash2, Check, Loader2 } from "lucide-react";
import { usePhotoInputs, PhotoUploadButton } from "@/components/shared/PhotoPicker";

// Tlačítko „Nahrát fotky“ jednoho řádku výkazu. Hook se musí volat per
// řádek (ne ve smyčce), proto samostatná komponenta. Jedno tlačítko: iOS
// nativní nabídka, Android Vyfotit / Galerie / Soubor, PC výběr souboru.
function RowPhotoTiles({ i, uploading, dragging, onBefore, onChange }) {
  const { inputs, open } = usePhotoInputs({
    accept: "image/jpeg,image/png,image/webp,image/heic,image/heif", onChange, disabled: uploading });
  return (
    <div className="basis-full">
      <PhotoUploadButton onClick={() => { onBefore(); open(); }} busy={uploading} dragging={dragging} testid={`day-close-photo-${i}`} />
      {inputs}
    </div>
  );
}
import { toast } from "sonner";

const STEP = 15;               // krok tlačítek −/+
const MIN_ROW = 1;             // řádek smí mít i jednu minutu — jinak nejde odeslat krátká směna
const OVERTIME_ASK_MIN = 9 * 60;   // nad 9 h se ptáme na důvod
const durHMM = (min) => `${Math.floor(Math.abs(min) / 60)}:${String(Math.abs(min) % 60).padStart(2, "0")} h`;

function DurStepper({ value, onChange, max = 24 * 60, testid }) {
  // −/+ po 15 min s podržením (rychlé opakování se zrychlením) + klik na
  // hodnotu = přímé zadání času (na mobilu nativní kolečkový výběr HH:MM).
  const holdRef = useRef(null);
  const valRef = useRef(value);
  valRef.current = value;
  const [editing, setEditing] = useState(false);
  const pad = (min) => `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
  const commit = (t) => {
    const [h, m] = (t || "").split(":").map(Number);
    if (!Number.isNaN(h) && !Number.isNaN(m)) onChange(Math.min(max, Math.max(MIN_ROW, h * 60 + m)));
    setEditing(false);
  };
  const stop = () => { if (holdRef.current) { clearTimeout(holdRef.current); holdRef.current = null; } };
  const start = (dir) => {
    const apply = () => onChange(dir < 0 ? Math.max(MIN_ROW, valRef.current - STEP) : Math.min(max, valRef.current + STEP));
    apply();
    let delay = 400;
    const loop = () => { apply(); delay = Math.max(70, delay * 0.82); holdRef.current = setTimeout(loop, delay); };
    holdRef.current = setTimeout(loop, delay);
  };
  const holdProps = (dir) => ({
    onPointerDown: (e) => { e.preventDefault(); start(dir); },
    onPointerUp: stop, onPointerLeave: stop, onPointerCancel: stop,
    onContextMenu: (e) => e.preventDefault(),
  });
  return (
    <div className="flex items-center rounded-[12px] border border-border overflow-hidden h-12" data-testid={testid}>
      <button type="button" {...holdProps(-1)}
        className="w-12 h-full grid place-items-center text-muted-foreground hover:bg-secondary shrink-0 select-none touch-none"><Minus size={16} /></button>
      {editing ? (
        <input type="time" autoFocus defaultValue={pad(value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") commit(e.currentTarget.value); }}
          className="flex-1 text-center text-[17px] font-bold tabular-nums border-x border-border/60 h-full min-w-[72px] bg-card outline-none"
          data-testid={`${testid}-input`} />
      ) : (
        <button type="button" onClick={() => setEditing(true)} title="Klikni a napiš čas"
          className="flex-1 text-center text-[17px] font-bold tabular-nums border-x border-border/60 h-full grid place-items-center min-w-[72px] hover:bg-secondary/60"
          data-testid={`${testid}-value`}>{durHMM(value)}</button>
      )}
      <button type="button" {...holdProps(1)}
        className="w-12 h-full grid place-items-center text-muted-foreground hover:bg-secondary shrink-0 select-none touch-none"><Plus size={16} /></button>
    </div>
  );
}

// Uzavření dne — blbuvzdorná verze: žádné od–do, žádné počítání.
// První zakázka dostane předvyplněný CELÝ odpracovaný čas, další zakázka
// automaticky ZBYTEK. Pauza se vybírá klikem. Přesčas = dvě velká tlačítka.
export default function DayCloseModal({ open, day, projects, breakRule, photosRequired = false, onOpenChange, onSaved }) {
  const [rows, setRows] = useState([]);
  const [otReason, setOtReason] = useState(null);      // "overtime" | "late_checkout"
  const [endFix, setEndFix] = useState("");            // "HH:MM" — skutečný konec při zapomenutém odhlášení
  const [busy, setBusy] = useState(false);
  const [delPhoto, setDelPhoto] = useState(null);   // {row, idx} — potvrzení mazání fotky
  const [lightbox, setLightbox] = useState(null);   // URL fotky v celoplošném náhledu
  const [uploading, setUploading] = useState(false);
  const [upProg, setUpProg] = useState(null);      // { row, done, total } během nahrávání
  // Čítač místo boolean: dragleave chodí při přejezdu přes vnitřní prvky,
  // s pouhým true/false by zvýraznění blikalo.
  const [dragRow, setDragRow] = useState(null);
  const dragDepth = useRef(0);
  const lastRowRef = useRef(0);                    // cíl pro Cmd+V u více zakázek
  const grossOf = (endIso) => Math.max(0, Math.round((new Date(endIso) - new Date(day.started_at)) / 60000));
  useEffect(() => {
    if (!open || !day) return;
    const gross = day.ended_at ? grossOf(day.ended_at) : 0;
    // uložené hodiny jsou po odečtu pauzy — pro editaci ukazujeme zpět hrubý čas
    const init = (day.allocations || []).length
      ? day.allocations.map((a) => ({ project_id: a.project_id || "", note: a.note || "", photos: a.photos || [],
          min: Math.max(MIN_ROW, Math.round((a.hours || 0) * 60) + (a.break_minutes || 0)), pinned: !!a.pinned }))
      : [{ project_id: "", note: "", photos: [], min: Math.max(MIN_ROW, gross), pinned: false }];
    setRows(init);
    setOtReason(day.overtime_reason || null);
    setEndFix("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, day?.id]);   // refetch nesmí přepsat rozepsaný výkaz
  const endIso = endFix ? `${day.date}T${endFix}` : day.ended_at;
  // Ukončená směna se počítá minimálně jako 1 minuta. Bez toho by se u směny
  // kratší než 30 s (grossOf zaokrouhluje na minuty) nedalo rozdělit vůbec nic.
  const grossMin = endIso ? Math.max(1, grossOf(endIso)) : 0;
  // Pracovník rozděluje CELOU dobu v práci; přestávku odečte systém.
  const brkMins = breakRule?.minutes ?? 30;
  const brkMinHours = breakRule?.minHours ?? breakRule?.min_hours ?? 6;
  // 181: o přestávce rozhoduje celý den — i dřívější směna téhož dne (odhlásil se a poslali ho zpátky)
  const otherMin = breakRule?.other_minutes || 0;
  const brkLeft = Math.max(0, brkMins - (breakRule?.other_break || 0));
  const autoBreak = (grossMin + otherMin) / 60 >= brkMinHours ? Math.min(brkLeft, Math.max(0, grossMin - 5)) : 0;
  const netMin = Math.max(0, grossMin - autoBreak);
  const allocMin = rows.reduce((s, r) => s + Math.max(0, r.min), 0);
  const remaining = grossMin - allocMin;
  const over = netMin > OVERTIME_ASK_MIN;
  // Po automatickém uzavření se ptáme vždy — pracovník musí říct, co se stalo.
  const mustExplain = !!day.auto_closed;
  const autoAtHHMM = day.auto_closed_at ? formatTime(day.auto_closed_at) : null;
  const rowOk = (r) => r.project_id !== "" && (r.note || "").trim() && r.min > 0;
  const photosOk = !photosRequired || rows.some((r) => (r.photos || []).length > 0);
  const canSubmit = rows.length > 0 && rows.every(rowOk) && remaining === 0 && (!over || !!otReason) && (!mustExplain || !!otReason) && photosOk;

  // Index řádku, který se dopočítává = POSLEDNÍ, na který dělník nesáhl.
  // Když sáhne na všechny, dopočet nemá kam jít a součet musí sedět ručně.
  const autoIdx = rows.reduce((acc, r, i) => (r.pinned ? acc : i), -1);

  // Zbytek naměřeného času nasype do dopočítávaného řádku. Ručně zadané řádky
  // (pinned) se nikdy nepřepisují — proto stačí upravit jeden a den se srovná sám.
  const balance = (rs) => {
    const ai = rs.reduce((acc, r, i) => (r.pinned ? acc : i), -1);
    if (ai < 0) return rs;
    const others = rs.reduce((s, r, i) => (i === ai ? s : s + Math.max(0, r.min)), 0);
    return rs.map((r, i) => (i === ai ? { ...r, min: Math.max(0, grossMin - others) } : r));
  };

  const setRow = (i, patch) => setRows((rs) => {
    const next = rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    // Sáhnout na hodiny = „zadáno ručně". Zakázka ani popis řádek nepřipínají.
    if (patch.min === undefined) return next;
    next[i] = { ...next[i], pinned: true };
    return balance(next);
  });

  const unpinRow = (i) => setRows((rs) => balance(rs.map((r, idx) => (idx === i ? { ...r, pinned: false } : r))));

  const removeRow = (i) => setRows((rs) => balance(rs.filter((_, x) => x !== i)));

  // Nový řádek je vždy nepřipnutý → stane se dopočítávaným a rovnou pobere zbytek.
  const addRow = () => setRows((rs) => balance([...rs, { project_id: "", note: "", photos: [], min: 0, pinned: false }]));

  // Když součet nesedí (všechny řádky ručně, nebo se změnil konec směny), uvolní
  // poslední řádek — ten se stane dopočítávaným a rozdíl si vezme na sebe.
  const absorbRest = () => setRows((rs) => (rs.length ? balance(rs.map((r, i) => (i === rs.length - 1 ? { ...r, pinned: false } : r))) : rs));

  const hintFor = (r, i) => (r.pinned ? "zadáno ručně — nemění se"
    : i === autoIdx ? "dopočítá se automaticky ze zbytku dne"
    : "uprav čas, nebo se dopočítá");

  // Další řádek přidáme jen když se naměřený čas dá rozdělit i mezi (n+1) řádků
  // po MIN_ROW — jinak by vznikl řádek s nulou, který nejde odeslat.
  const canAddRow = grossMin >= (rows.length + 1) * MIN_ROW;
  // Když se změní naměřený čas (oprava konce směny přes „Zapomněl jsem se
  // odhlásit"), dopočítávaný řádek se musí srovnat sám — hint „dopočítá se
  // automaticky" jinak lže a odeslání zbytečně blokuje červená hláška.
  useEffect(() => {
    setRows((rs) => (rs.some((r) => !r.pinned) ? balance(rs) : rs));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grossMin]);
  // Jeden vstup pro výběr souborů, přetažení myší i Cmd+V. Náhledy naskakují
  // postupně po každé nahrané fotce a průběh je vidět v kartě pod dlaždicemi.
  const addFiles = async (i, list) => {
    const all = [...(list || [])];
    const imgs = all.filter((f) => (f?.type || "").startsWith("image/") || /\.(heic|heif)$/i.test(f?.name || ""));
    if (imgs.length < all.length) toast.error("Lze nahrát jen fotky (JPG, PNG, HEIC) — ostatní soubory byly vynechány.");
    if (!imgs.length) return;
    lastRowRef.current = i;
    setUploading(true); setUpProg({ row: i, done: 0, total: imgs.length });
    // 216: až tři fotky naráz. Náhledy dál naskakují postupně a v pořadí výběru:
    // hotová fotka se připojí, až jsou hotové i všechny před ní.
    const got = new Array(imgs.length);
    let next = 0;
    const { failed } = await uploadFiles(imgs, {
      prepare: compressImage,
      onResult: (res, k) => {
        got[k] = res.ok ? res.url : null;
        const add = [];
        while (next < imgs.length && got[next] !== undefined) { if (got[next]) add.push(got[next]); next += 1; }
        if (add.length) setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, photos: [...r.photos, ...add] } : r)));
        if (!res.ok) console.error("DayCloseModal upload failed", { name: res.file?.name, type: res.file?.type, size: res.file?.size, err: res.error });
      },
      onEach: (done) => setUpProg({ row: i, done, total: imgs.length }),
    });
    if (failed.length) toast.error(`Fotku se nepodařilo nahrát: ${apiError(failed[0].error)}`);
    setUploading(false); setUpProg(null);
  };
  const addPhotos = (i, e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    addFiles(i, files);
  };

  // Cmd+V: screenshot nebo zkopírovaná fotka rovnou do výkazu — u jediné
  // zakázky do ní, jinak do naposledy rozpracované.
  useEffect(() => {
    if (!open) return;
    const onPaste = (e) => {
      const files = [...(e.clipboardData?.files || [])];
      if (files.length) { e.preventDefault(); addFiles(rows.length === 1 ? 0 : lastRowRef.current, files); }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rows.length]);
  const payload = () => {
    const p = {
      allocations: rows.map((r) => ({ project_id: r.project_id || null, hours: r.min / 60, note: r.note, photos: r.photos, pinned: !!r.pinned })),
    };
    if (endFix) { p.overtime_reason = otReason || "late_checkout"; p.ended_at = `${day.date}T${endFix}`; }
    else if (otReason) { p.overtime_reason = otReason; }
    return p;
  };
  const saveDraft = async () => {
    setBusy(true);
    try { await api.patch(`/daysheet/${day.id}`, payload()); toast.success("Výkaz uložen jako rozpracovaný."); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/daysheet/${day.id}`, payload());
      // Už odeslaný výkaz se PATCHem jen aktualizuje (segmenty se přepíšou na backendu).
      // Znovu volat /submit nesmíme — ten endpoint bere jen draft/returned a vrátil by 400.
      if (day.status !== "submitted") await api.post(`/daysheet/${day.id}/submit`, {});
      toast.success(day.status === "submitted" ? "Výkaz upraven." : "Výkaz odeslán ke schválení."); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && rows.length && day.status !== "submitted") saveDraft(); else onOpenChange(o); }}>
      <DialogContent sheet data-testid="day-close-modal" onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => { if (lightbox) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (lightbox) { e.preventDefault(); setLightbox(null); } }}
        className="max-w-2xl md:max-h-[92vh] p-0 max-md:pb-0 gap-0 overflow-hidden [&>*]:min-w-0
          grid-rows-[auto_minmax(0,1fr)_auto]">
        <DialogHeader className="text-left pl-4 sm:pl-6 pr-14 pt-1 md:pt-6 pb-3 border-b border-border">
          <DialogTitle className="text-[19px] sm:text-[22px] font-extrabold tracking-tight">Zkontroluj svůj den</DialogTitle>
          <p className="text-[13px] text-muted-foreground tabular-nums">
            {formatDate(day.date)}{day.ended_at ? <> · v práci {formatTime(day.started_at)}–{endFix || formatTime(day.ended_at)}</> : null}
          </p>
          <div className="flex items-end justify-between gap-3 pt-2 min-w-0">
            <span className="text-[13px] text-muted-foreground min-w-0">
              Rozděleno podle naměřeného času — jen potvrď nebo uprav
            </span>
            <span className="text-[22px] font-extrabold tabular-nums whitespace-nowrap leading-none" data-testid="day-close-total">{durHMM(allocMin)}</span>
          </div>
        </DialogHeader>
        <div className="overflow-y-auto overflow-x-hidden px-4 sm:px-6 py-4 space-y-4 min-w-0">
        {/* Přesčas / zapomenuté odhlášení — vždy po automatickém uzavření */}
        {(over || mustExplain) && (
          <div className={`rounded-[12px] border p-3 sm:p-3.5 space-y-2.5 min-w-0 ${mustExplain ? "border-destructive/40 bg-destructive/5" : "border-brand/30 bg-brand/5"}`} data-testid="overtime-block">
            <p className="text-sm font-bold">
              {mustExplain
                ? <>Nebyl jsi odhlášen — systém směnu ukončil{autoAtHHMM ? ` v ${autoAtHHMM}` : ""}. Co se stalo?</>
                : "Směna je delší než 9 hodin. Co se stalo?"}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button type="button" onClick={() => { setOtReason("overtime"); setEndFix(""); }} data-testid="ot-overtime"
                className={`h-12 rounded-[12px] border text-sm font-bold px-3 ${otReason === "overtime" ? "bg-brand text-white border-brand" : "bg-card border-border hover:border-brand/50"}`}>
                Dělal jsem přesčas
              </button>
              <button type="button" onClick={() => setOtReason("late_checkout")} data-testid="ot-late"
                className={`h-12 rounded-[12px] border text-sm font-bold px-3 ${otReason === "late_checkout" ? "bg-brand text-white border-brand" : "bg-card border-border hover:border-brand/50"}`}>
                Zapomněl jsem se odhlásit
              </button>
            </div>
            {(otReason === "late_checkout" || (mustExplain && otReason === "overtime")) && (
              <div>
                <div className="text-[13px] font-semibold mb-1">V kolik jsi doopravdy skončil?</div>
                <Input type="time" step={300} value={endFix} onChange={(e) => setEndFix(e.target.value)}
                  className="h-12 w-full min-w-0 sm:w-40" data-testid="ot-end-fix" />
                <p className="text-[12px] text-muted-foreground mt-1">
                  {otReason === "overtime"
                    ? "U přesčasu můžeš uvést i čas po automatickém odhlášení. Vedoucí uvidí, že jde o přesčas nad rámec naměřeného času."
                    : `Zapomenuté odhlášení jde jen zpřesnit dozadu${autoAtHHMM ? ` — nejpozději ${autoAtHHMM}` : ""}.`}
                </p>
              </div>
            )}
          </div>
        )}
        {/* Stav rozdělení */}
        <div className={`rounded-[12px] border px-3.5 sm:px-4 py-3 flex items-center justify-between gap-2 sm:gap-3 flex-wrap min-w-0 ${remaining === 0 ? "bg-success/10 border-success/30" : "bg-destructive/10 border-destructive/30"}`} data-testid="day-close-remaining">
          <span className={`text-sm font-bold ${remaining === 0 ? "text-success" : "text-destructive"}`}>
            {remaining === 0
              ? "Vše rozděleno ✓"
              : `Součet nesedí — máš zapsáno o ${durHMM(remaining)} ${remaining < 0 ? "víc" : "míň"}, než jsi byl v práci`}
          </span>
          <span className="text-xs text-muted-foreground tabular-nums">v práci {durHMM(grossMin)}{autoBreak > 0 ? ` · přestávka ${autoBreak} min se odečte · zaplaceno ${durHMM(netMin)}` : ""}</span>
          {remaining !== 0 && rows.length > 0 && (
            <button type="button" onClick={absorbRest} data-testid="absorb-rest"
              className="h-9 px-3 rounded-full bg-brand text-white text-[13px] font-bold whitespace-nowrap">
              Srovnat na naměřený čas
            </button>
          )}
        </div>
        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={i} className="rounded-[14px] border border-border p-3 sm:p-4" data-testid={`day-close-row-${i}`}>
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap min-w-0">
                <span className="flex items-center gap-2 text-sm font-bold min-w-0">
                  <span className="w-6 h-6 grid place-items-center rounded-[6px] bg-secondary text-muted-foreground text-xs shrink-0">{i + 1}</span>
                  <span className="truncate">{projects.find((p) => p.id === r.project_id)?.name || "Vyber zakázku"}</span>
                  {r.pinned ? (
                    <button type="button" onClick={() => unpinRow(i)} data-testid={`day-close-unpin-${i}`}
                      title="Uvolnit — hodiny se zase dopočítají"
                      className="inline-flex items-center gap-1 h-7 px-2 rounded-full bg-brand/10 text-brand text-[11px] font-bold whitespace-nowrap">
                      ✎ ručně <X size={11} />
                    </button>
                  ) : i === autoIdx ? (
                    <span className="inline-flex items-center h-7 px-2 rounded-full bg-secondary text-muted-foreground text-[11px] font-bold whitespace-nowrap">dopočítá se</span>
                  ) : null}
                </span>
                {rows.length > 1 && <button onClick={() => removeRow(i)} className="text-[13px] font-bold text-muted-foreground hover:text-destructive shrink-0"><Trash2 size={13} className="inline mr-1" />Smazat</button>}
              </div>
              <div className="space-y-3">
                <Select value={r.project_id || ""} onValueChange={(v) => setRow(i, { project_id: v })}>
                  <SelectTrigger className="h-12 rounded-[12px]" data-testid={`day-close-project-${i}`}><SelectValue placeholder="Vyberte zakázku" /></SelectTrigger>
                  <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-[.1em] text-muted-foreground mb-1">Kolik hodin</div>
                  <DurStepper value={r.min} onChange={(v) => setRow(i, { min: v })} max={grossMin} testid={`day-close-dur-${i}`} />
                  <div className="text-[12px] text-muted-foreground mt-1">{hintFor(r, i)}</div>
                </div>
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-[.1em] text-muted-foreground mb-1">Co se dělalo <span className="text-destructive normal-case">· povinné</span></div>
                  <Textarea value={r.note} onChange={(e) => setRow(i, { note: e.target.value })} rows={3} className="rounded-[12px]" placeholder="Např. betonáž základové desky, sekce B…" data-testid={`day-close-note-${i}`} />
                </div>
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-[.1em] text-muted-foreground mb-1">Fotky {photosRequired ? <span className="text-destructive normal-case">· povinné (aspoň 1 za den)</span> : <span className="normal-case font-normal">· nepovinné</span>}{r.photos.length > 0 && <span className="normal-case font-normal"> · {r.photos.length} nahrané</span>}</div>
                  <div className={`flex flex-wrap gap-2 rounded-[12px] transition-colors ${dragRow === i ? "outline-dashed outline-2 outline-brand bg-brand/10 p-2" : ""}`}
                    onDragEnter={(e) => { e.preventDefault(); dragDepth.current += 1; setDragRow(i); }}
                    onDragOver={(e) => e.preventDefault()}
                    onDragLeave={() => { dragDepth.current -= 1; if (dragDepth.current <= 0) { dragDepth.current = 0; setDragRow(null); } }}
                    onDrop={(e) => { e.preventDefault(); dragDepth.current = 0; setDragRow(null); addFiles(i, e.dataTransfer?.files); }}>
                    {r.photos.map((url, pi) => (
                      <div key={pi} className="relative w-24 h-24 md:w-28 md:h-28 rounded-[12px] overflow-hidden border border-border">
                        <button type="button" onClick={() => setLightbox(url)} className="w-full h-full" aria-label="Zvětšit fotku">
                          <SignedImg url={url} alt="" thumb={112} className="w-full h-full object-cover" />
                        </button>
                        <button onClick={() => setDelPhoto({ row: i, idx: pi })} aria-label="Smazat fotku" className="absolute top-1 right-1 bg-destructive text-white rounded-full w-7 h-7 grid place-items-center"><X size={14} /></button>
                      </div>
                    ))}
                    <RowPhotoTiles i={i} uploading={uploading} dragging={dragRow === i}
                      onBefore={() => { lastRowRef.current = i; }} onChange={(e) => addPhotos(i, e)} />
                  </div>
                  <p className="hidden md:block text-[11px] text-muted-foreground mt-1.5">Fotky sem můžeš i přetáhnout myší nebo vložit přes Cmd+V.</p>
                  {upProg?.row === i && (
                    <div className="mt-2 rounded-[12px] border border-brand/30 bg-brand/5 p-3" data-testid={`day-close-upload-progress-${i}`}>
                      <div className="flex items-center gap-2 text-sm font-bold text-brand">
                        <Loader2 size={16} className="animate-spin motion-reduce:animate-none" /> Nahrávám fotku {Math.min(upProg.done + 1, upProg.total)} z {upProg.total}
                      </div>
                      <div className="h-2 rounded-full bg-secondary overflow-hidden mt-2">
                        <div className="h-full bg-brand transition-all" style={{ width: `${Math.round((upProg.done / Math.max(upProg.total, 1)) * 100)}%` }} />
                      </div>
                      <p className="text-[12px] text-muted-foreground mt-1.5">Náhledy naskakují postupně — nezavírej okno.</p>
                    </div>
                  )}
                  {delPhoto?.row === i && (
                    <div className="mt-2 rounded-[12px] border border-destructive/30 bg-destructive/5 p-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid={`day-close-del-photo-${i}`}>
                      <span className="text-[13px] font-bold">Smazat fotku?</span>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setDelPhoto(null)} className="h-10 flex-1 sm:flex-none font-bold">Zpět</Button>
                        <Button onClick={() => { setRow(i, { photos: r.photos.filter((_, x) => x !== delPhoto.idx) }); setDelPhoto(null); }}
                          className="h-10 flex-1 sm:flex-none bg-destructive hover:bg-destructive/90 text-white font-bold">Smazat</Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          {canAddRow && (
            <button onClick={addRow} className="w-full h-12 rounded-[12px] border-2 border-dashed border-border text-sm font-bold text-muted-foreground hover:border-brand hover:text-brand" data-testid="day-close-add-row">+ Přidat další zakázku{remaining > 0 ? ` (${durHMM(remaining)})` : ""}</button>
          )}
        </div>
        </div>
        {/* Patička — pořád na očích, primární akce pod palcem */}
        <div className="border-t border-border bg-card px-4 sm:px-6 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] space-y-2">
          <div className="flex md:justify-end">
            <Button onClick={submit} disabled={busy || uploading || !canSubmit} data-testid="day-close-submit"
              className="h-[54px] md:h-12 w-full md:w-auto md:px-8 rounded-full bg-success hover:bg-success/90 text-white font-extrabold text-base disabled:opacity-45">
              <Check size={18} /> {day.status === "submitted" ? "Uložit změny" : "Souhlasí · odeslat ke schválení"}
            </Button>
          </div>
          {photosRequired && !photosOk && <p className="text-[12px] text-destructive font-semibold" data-testid="day-close-photos-required">Přidejte aspoň jednu fotku z dnešní práce — vaše firma to vyžaduje.</p>}
          <div className="flex items-center justify-between gap-3 min-w-0">
            <span className="text-[12px] text-muted-foreground min-w-0 truncate">
              {rows.filter((r) => r.project_id).length} {rows.length === 1 ? "zakázka" : rows.length < 5 ? "zakázky" : "zakázek"} · celkem {durHMM(allocMin)}
            </span>
            {day.status !== "submitted" && (
              <button type="button" onClick={saveDraft} disabled={busy} data-testid="day-close-save-draft"
                className="text-[13px] font-bold text-muted-foreground hover:text-foreground shrink-0 disabled:opacity-50">
                Uložit rozpracované
              </button>
            )}
          </div>
        </div>
      </DialogContent>
      <PhotoLightbox url={lightbox} onClose={() => setLightbox(null)} />
    </Dialog>
  );
}
